import { useState } from 'react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { DollarSign, Lock, Pencil, Plus, X } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/app/components/ui/tooltip';
import {
  useTechPayRates,
  useDeleteTechPayRate,
  usePayrollSettings,
} from '@/hooks/use-payroll';
import { usePermissions } from '@/hooks/use-permissions';
import { usePersons } from '@/hooks/use-persons';
import { useProjects } from '@/hooks/use-projects';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { TechPayRate, Person } from '@/lib/types';
import { describeOvertimeRule } from '../settings/overtime-rule';
import { PayRateForm } from './pay-rate-form';

export function PayRatesPage() {
  const orgCurrency = useOrgCurrency();
  const permissions = usePermissions();
  const canManagePayroll = permissions.hasMinModuleRole('payroll', 'manager');
  const [selectedPerson, setSelectedPerson] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState<TechPayRate | null>(null);

  const projectFilter: number | 'none' | undefined = selectedProject === 'defaults'
    ? 'none'
    : selectedProject === 'all'
      ? undefined
      : Number(selectedProject);
  const filters = {
    ...(selectedPerson ? { person: Number(selectedPerson) } : {}),
    ...(projectFilter !== undefined ? { project: projectFilter } : {}),
  };
  const { data: rates, isLoading, error } = useTechPayRates(filters);
  const forbidden = isAxiosError(error) && error.response?.status === 403;
  const { data: payrollSettings } = usePayrollSettings();
  const overtimeRule = payrollSettings ? describeOvertimeRule(payrollSettings) : null;
  const { data: personsData } = usePersons({ page_size: 100 });
  const { data: projectsData } = useProjects({ page_size: 1000 });
  const deleteRate = useDeleteTechPayRate();

  const handleDelete = (id: number) => {
    if (confirm('Delete this pay rate record?')) {
      deleteRate.mutate(id);
    }
  };

  const personList = personsData?.results ?? [];
  const projectList = projectsData?.results ?? [];
  const visibleRates = rates?.filter((rate) => {
    if (selectedProject === 'defaults') return rate.project == null;
    if (selectedProject !== 'all') return rate.project === Number(selectedProject);
    return true;
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Pay Rates</h1>
          <p className="text-gray-600 mt-1">Manage technician pay rates and rate history</p>
        </div>
        {canManagePayroll && !forbidden && (
          <Button
            onClick={() => {
              setEditingRate(null);
              setShowForm(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Rate
          </Button>
        )}
      </div>

      {forbidden ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Lock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>You don&apos;t have access to pay rates</p>
            <p className="text-sm mt-1">Pay rates are managed by H3Pay Managers and organization Admins.</p>
          </CardContent>
        </Card>
      ) : (
      <>

      {/* Filter */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="pay-rates-worker-filter">Filter by Worker</Label>
              <Select value={selectedPerson || 'all'} onValueChange={(v) => setSelectedPerson(v === 'all' ? '' : v)}>
                <SelectTrigger id="pay-rates-worker-filter">
                  <SelectValue placeholder="All Workers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Workers</SelectItem>
                  {(Array.isArray(personList) ? personList : []).map((p: Person) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="pay-rates-job-filter">Filter by Job</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger id="pay-rates-job-filter">
                  <SelectValue placeholder="All jobs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="defaults">Defaults only</SelectItem>
                  {(Array.isArray(projectList) ? projectList : []).map((project) => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {canManagePayroll && showForm && (
        <PayRateForm
          key="add"
          onDone={() => setShowForm(false)}
        />
      )}
      {canManagePayroll && editingRate && (
        <PayRateForm
          key={editingRate.id}
          rate={editingRate}
          onDone={() => setEditingRate(null)}
        />
      )}

      {/* Rates Table */}
      <Card>
        <CardHeader>
          <CardTitle>{selectedPerson ? 'Rate History' : 'Current Rates'}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading rates...</div>
          ) : error ? (
            <div className="p-8 text-center text-gray-500">Couldn&apos;t load pay rates. Please try again.</div>
          ) : !visibleRates?.length ? (
            <div className="p-8 text-center text-gray-500">
              <DollarSign className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No pay rates configured yet</p>
              {canManagePayroll && (
                <Button
                  className="mt-4"
                  variant="outline"
                  onClick={() => {
                    setEditingRate(null);
                    setShowForm(true);
                  }}
                >
                  Add a rate
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="px-4 pb-2">Worker</th>
                    <th className="px-4 pb-2">Job</th>
                    <th className="px-4 pb-2">Effective Date</th>
                    <th className="px-4 pb-2 text-right">Hourly</th>
                    <th className="px-4 pb-2 text-right">Overtime</th>
                    <th className="px-4 pb-2 text-right">Daily</th>
                    <th className="px-4 pb-2 text-right">Per Diem</th>
                    <th className="px-4 pb-2">Notes</th>
                    <th className="px-4 pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRates.map((rate) => (
                    <tr key={rate.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">
                        {rate.person == null && rate.tech == null
                          ? 'Everyone on this job'
                          : rate.person_name ?? rate.tech_name ?? 'Unknown'}
                      </td>
                      <td className="px-4 py-2">{rate.project_name ?? 'Default'}</td>
                      <td className="px-4 py-2">{rate.effective_date}</td>
                      <td className="px-4 py-2 text-right">
                        {rate.hourly_rate ? formatMoney(rate.hourly_rate, orgCurrency) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {rate.overtime_rate ? (
                          formatMoney(rate.overtime_rate, orgCurrency)
                        ) : isDailyRatePaid(rate) ? (
                          <span className="text-gray-500 whitespace-nowrap">— Daily rate, no overtime</span>
                        ) : (
                          <AutoOvertimeCell rule={overtimeRule} />
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {rate.daily_rate ? formatMoney(rate.daily_rate, orgCurrency) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {rate.per_diem_rate ? formatMoney(rate.per_diem_rate, orgCurrency) : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-500 truncate max-w-[200px]">{rate.notes}</td>
                      <td className="px-4 py-2">
                        {canManagePayroll && (
                          <div className="flex items-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="Edit rate"
                              onClick={() => {
                                setShowForm(false);
                                setEditingRate(rate);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              aria-label="Delete rate"
                              onClick={() => handleDelete(rate.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}

function isDailyRatePaid(rate: Pick<TechPayRate, 'daily_rate'>): boolean {
  const dailyRate = Number(rate.daily_rate);
  return Number.isFinite(dailyRate) && dailyRate !== 0;
}

function AutoOvertimeCell({ rule }: { rule: string | null }) {
  if (!rule) return <>Auto</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to="/payroll/settings"
          className="inline-block text-right whitespace-nowrap hover:underline"
          aria-label={`Auto — ${rule}. Open Payroll Settings`}
        >
          Auto
          <span className="block text-xs text-gray-500">{rule}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="left">
        Auto follows the organization&apos;s overtime rule. Click to open Pay → Settings. State
        overtime rules, where configured, take precedence.
      </TooltipContent>
    </Tooltip>
  );
}
